import { type ReactElement, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { type RefundApprovalView, type SupportTicketDetail } from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { formatMoney } from '@vello/format';
import {
  maySelfApprove,
  REFUND_QUEUE_STATUSES,
  formatMarketInstant,
  marketTimeZone,
  refundApprovalBlock,
  refundAuthority,
  refundRejectable,
  refundStatusTone,
  refundSubject,
  summarizeRefundQueue,
  type RefundQueueStatus,
} from '../../src/index';
import { SelfApprovalNote } from '../components/self-approval-note';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { useCountryName } from '../i18n/country-name';
import { AUTO_REFRESH_MS, useAutoRefresh, Badge, Button, Field, Notice, PageHeader, Panel, Select, StatCard, Table, TextInput, FormRow, EmptyState, Cluster, Grid, Stack, Text, Divider, NoteText, PanelBody, Drawer, DrawerHeader, DrawerBody, Tabs, TabPanel, KeyValueList, SectionTitle } from '@vello/web-theme';
import { PersonName } from '../components/person-name';
import { DrawerStack } from '../components/drawer-stack';
import { SubjectCard } from '../components/subject-card';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const POLICY_RE = /^finance\.refundFourEyesThreshold@country:([A-Za-z]{2})#v(\d+)$/;

type DrawerTab = 'request' | 'subject' | 'history';
type TicketRead = { readonly ref: string; readonly state: 'loading' } | { readonly ref: string; readonly state: 'ok'; readonly detail: SupportTicketDetail } | { readonly ref: string; readonly state: 'refused' } | { readonly ref: string; readonly state: 'error'; readonly text: string };

/** The subject of an item: the three the server writes plus a ride dispute (`dispute:<id>`, its ride in the payload). */
function subjectOf(r: RefundApprovalView): { readonly kind: 'food' | 'support' | 'statement' | 'dispute' | 'other'; readonly ref: string } {
  const s = refundSubject(r.subjectRef);
  if (s.kind !== 'other') return s;
  return r.subjectRef.startsWith('dispute:') ? { kind: 'dispute', ref: r.subjectRef.slice('dispute:'.length) } : s;
}

/**
 * Finance › Aprovações de reembolso (W6-REFUND-FOUR-EYES, D398, ADR-0063) — the four-eyes queue of refunds and credits
 * above the governed threshold (`GET /v1/refund-approvals`, `refund:view`; `POST …/:id/decision`, `refund:approve`).
 *
 * - Each item says what it is (a food-order refund or chargeback, a support refund or coupon), what it is about, how
 *   much (tabular numerals, formatted from its OWN currency — totals are per currency, never summed across), which
 *   governed threshold routed it and why, who asked and when, and who decided.
 * - Approve is hidden on one's own item, with the reason shown — the server refuses `SELF_APPROVAL` anyway, whatever
 *   the grant; the maker may only withdraw. Every decision needs a reason. An approval above the checker's grant (in
 *   the item's currency) is refused by the server and explained here.
 * - A failed read is an ERROR ("unknown"), never an empty queue or a zero total.
 * - No primary action: the decisions are per row (secondary/danger), and there is nothing to create here — items are
 *   born by the money routes themselves.
 */
export function RefundApprovalsScreen(): ReactElement {
  const { runtime, me, grants, markets } = useAdmin();
  const selfApproval = maySelfApprove(grants); // D868: a GLOBAL super administrator decides their own proposals
  const { t, lang } = useT();
  const { v, fail, explain, has, reason: routeReason } = useVocab();
  const { navigate } = useRouter();
  const countryName = useCountryName();
  const operatorId = me?.id;
  const { mayDecide } = refundAuthority(grants);
  const [status, setStatus] = useState<RefundQueueStatus>('PENDING');
  const [loaded, setLoaded] = useState<RefundQueueStatus | undefined>(undefined);
  const [rows, setRows] = useState<readonly RefundApprovalView[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [reason, setReason] = useState('');
  const [msg, setMsg] = useState<{ tone: 'success' | 'danger' | 'warning'; text: string } | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const readSeq = useRef(0);
  const [drawerRow, setDrawerRow] = useState<RefundApprovalView | undefined>(undefined);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState<DrawerTab>('request');
  const [ticketRead, setTicketRead] = useState<TicketRead | undefined>(undefined);
  const ticketSeq = useRef(0);

  const money = useCallback(
    (m: { readonly amountMinor: string; readonly currency: string }): string =>
      /^-?\d+$/.test(m.amountMinor) && isIso4217Currency(m.currency) ? formatMoney({ amountMinor: BigInt(m.amountMinor), currency: m.currency }, { locale: lang, timeZone: 'UTC', measurement: 'metric' }) : `${m.amountMinor} ${m.currency}`,
    [lang],
  );
  const refusal = (e: { reasonCode?: string; code: string }): string => explain(e) ?? t('ui.actionFailed', { code: fail(e) });

  const load = useCallback(
    async (s: RefundQueueStatus): Promise<void> => {
      const seq = ++readSeq.current;
      setLoaded(s);
      const res = await runtime.refundApprovals.list({ status: s });
      if (seq !== readSeq.current) return;
      if (res.ok) {
        setRows(res.value.approvals);
        setError(undefined);
      } else setError(t('refunds.error.queue', { code: fail(res.error) }));
    },
    [runtime, t],
  );

  // The queue follows the chosen status by itself (the skeleton returns only on a status change) and stays current.
  useEffect(() => {
    setRows(undefined);
    void load(status);
  }, [status, load]);
  useAutoRefresh(() => load(status), { intervalMs: AUTO_REFRESH_MS.queue });

  const openDrawer = (row: RefundApprovalView): void => {
    setDrawerRow(row);
    setDrawerTab('request');
    setDrawerOpen(true);
    const subject = subjectOf(row);
    const seq = ++ticketSeq.current;
    if (subject.kind !== 'support') {
      setTicketRead(undefined);
      return;
    }
    setTicketRead({ ref: subject.ref, state: 'loading' });
    void runtime.supportDesk.ticket(subject.ref).then((res) => {
      if (seq !== ticketSeq.current) return;
      if (res.ok) setTicketRead({ ref: subject.ref, state: 'ok', detail: res.value });
      else if (res.error.status === 403 || res.error.status === 404) setTicketRead({ ref: subject.ref, state: 'refused' });
      else setTicketRead({ ref: subject.ref, state: 'error', text: t('refunds.drawer.subject.error', { code: fail(res.error) }) });
    });
  };

  const decide = async (row: RefundApprovalView, decision: 'APPROVE' | 'REJECT'): Promise<void> => {
    setBusy(true);
    setMsg(undefined);
    const res = await runtime.refundApprovals.decide(row.approvalId, { decision, reason: reason.trim() });
    setBusy(false);
    if (res.ok) {
      const decided = res.value.approval;
      const text = decided.status === 'WITHDRAWN'
        ? t('refunds.withdrawn', { amount: money(decided.amount) })
        : decided.status === 'REJECTED'
          ? t('refunds.rejected', { amount: money(decided.amount) })
          : t((decided.executionRef ?? '').startsWith('intent:') ? 'refunds.approved.intent' : 'refunds.approved', { amount: money(decided.amount) });
      setMsg({ tone: 'success', text });
      setReason('');
      if (loaded !== undefined) await load(loaded);
      return;
    }
    const code = res.error.reasonCode ?? res.error.detail;
    setMsg({ tone: code === 'FINANCIAL_LIMIT_EXCEEDED' || code === 'FINANCIAL_LIMIT_CURRENCY_MISMATCH' ? 'warning' : 'danger', text: refusal(res.error) });
    if (code === 'REFUND_NOT_PENDING' && loaded !== undefined) await load(loaded);
  };

  const hasReason = reason.trim() !== '';
  const totals = rows !== undefined ? summarizeRefundQueue(rows) : [];
  const when = (instant: string | null, marketId: string | null): string => formatMarketInstant(instant ?? undefined, marketTimeZone(marketId ?? undefined, markets), lang);
  const actor = (id: string | null, label: string | null): ReactNode => {
    if (id === null) return undefined;
    if (operatorId !== undefined && id.toLowerCase() === operatorId.toLowerCase()) return t('refunds.actor.you');
    return <PersonName id={id} {...(label !== null ? { fallback: label } : {})} />;
  };
  /** The governed limit's key in words ("Double-approval limit of Brazil, version 3") — the key itself never reaches the screen. */
  const limitWords = (policy: string | null): string | undefined => {
    if (policy === null) return undefined;
    const m = POLICY_RE.exec(policy);
    if (m !== null) return t('refunds.limit.country', { country: countryName(m[1]), version: m[2] ?? '' });
    const version = /#v(\d+)$/.exec(policy)?.[1];
    return version !== undefined ? t('refunds.limit.generic', { version }) : t('refunds.limit.genericNoVersion');
  };
  const subjectLabel = (r: RefundApprovalView): string => {
    const s = subjectOf(r);
    return t(`refunds.subject.${s.kind}`, { ref: s.ref.length > 12 ? `${s.ref.slice(0, 8)}…` : s.ref });
  };

  const renderDrawer = (r: RefundApprovalView): ReactElement => {
    const subject = subjectOf(r);
    const detail = ticketRead !== undefined && ticketRead.ref === subject.ref && ticketRead.state === 'ok' ? ticketRead.detail : undefined;
    const tk = detail?.ticket;
    const zone = marketTimeZone((tk?.marketId ?? r.marketId) ?? undefined, markets);
    const reasonWords = has('support.compensation_reason', r.reasonCode) ? v('support.compensation_reason', r.reasonCode) : undefined;
    const rideId = subject.kind === 'support' ? tk?.rideId : typeof r.details['rideId'] === 'string' && UUID_RE.test(r.details['rideId']) ? r.details['rideId'] : undefined;
    const limit = limitWords(r.thresholdPolicy);
    const outcome = (r.executionRef ?? '').startsWith('settlement:') ? 'settlement' : (r.executionRef ?? '').startsWith('credit-note:') ? 'creditNote' : (r.executionRef ?? '').startsWith('intent:') ? 'intent' : 'none';
    return (
      <>
        <DrawerHeader
          titleId="refund-drawer-title"
          title={v('payments.refund_approval_kind', r.kind)}
          subtitle={<span className="tnum">{money(r.amount)}</span>}
          actions={<Badge tone={refundStatusTone(r.status)}>{v('refund.approval_status', r.status)}</Badge>}
          onClose={() => setDrawerOpen(false)}
          closeLabel={t('ui.close')}
        >
          <Tabs
            id="refund-drawer"
            ariaLabel={t('refunds.drawer.tabs')}
            value={drawerTab}
            onChange={setDrawerTab}
            tabs={[
              { id: 'request', label: t('refunds.drawer.tab.request') },
              { id: 'subject', label: t('refunds.drawer.tab.subject') },
              { id: 'history', label: t('refunds.drawer.tab.history') },
            ]}
          />
        </DrawerHeader>
        <DrawerBody>
          <DrawerStack>
            <TabPanel id="refund-drawer" tab="request" value={drawerTab}>
              <KeyValueList
                items={[
                  { label: t('refunds.drawer.kind'), value: v('payments.refund_approval_kind', r.kind) },
                  { label: t('refunds.drawer.amount'), value: money(r.amount), mono: true },
                  { label: t('refunds.drawer.status'), value: v('refund.approval_status', r.status) },
                  { label: t('refunds.drawer.requestedBy'), value: actor(r.requestedBy, r.requestedByLabel) },
                  { label: t('refunds.drawer.requestedAt'), value: when(r.requestedAt, r.marketId), mono: true },
                  { label: t('refunds.drawer.reason'), value: [reasonWords, r.note].filter((x): x is string => x !== undefined && x !== null && x !== '').join(' — ') || t('refunds.drawer.reason.none') },
                  { label: t('refunds.drawer.route'), value: routeReason(r.routeReason) },
                  { label: t('refunds.drawer.limit'), value: r.threshold !== null ? `${money(r.threshold)}${limit !== undefined ? ` · ${limit}` : ''}` : (limit ?? t('refunds.threshold.none')) },
                ]}
              />
            </TabPanel>
            <TabPanel id="refund-drawer" tab="subject" value={drawerTab}>
              <Stack gap={4}>
                <SectionTitle>{subjectLabel(r)}</SectionTitle>
                {subject.kind === 'support' && ticketRead?.state === 'loading' && <Text as="p" tone="secondary">{t('refunds.drawer.subject.loading')}</Text>}
                {subject.kind === 'support' && ticketRead?.state === 'refused' && <Notice tone="neutral">{t('desk.detail.notFound')}</Notice>}
                {subject.kind === 'support' && ticketRead?.state === 'error' && <Notice tone="warning">{ticketRead.text}</Notice>}
                {subject.kind !== 'support' && <Notice tone="neutral">{t('refunds.drawer.subject.noRead')}</Notice>}
                {tk !== undefined && (
                  <>
                    <KeyValueList
                      items={[
                        { label: t('refunds.drawer.ticket.category'), value: v('support.support_category', tk.category) },
                        { label: t('refunds.drawer.ticket.status'), value: v('support.ticket_status', tk.status) },
                        { label: t('refunds.drawer.ticket.priority'), value: v('support.ticket_priority', tk.priority) },
                        { label: t('refunds.drawer.ticket.openedAt'), value: formatMarketInstant(tk.createdAt, zone, lang), mono: true },
                        ...(tk.openedBy !== undefined ? [{ label: t('refunds.drawer.ticket.openedBy'), value: <PersonName id={tk.openedBy} /> }] : []),
                      ]}
                    />
                    {tk.compensation !== undefined && <Notice tone="warning">{t('refunds.drawer.ticket.compensation', { reason: v('support.compensation_reason', tk.compensation.reasonCode) })}</Notice>}
                  </>
                )}
                {subject.kind === 'support' && (
                  <Button variant="secondary" block icon="arrowRight" onClick={() => navigate('case_detail', { id: subject.ref })}>{t('refunds.drawer.ticket.open')}</Button>
                )}
                {(subject.kind === 'support' ? tk !== undefined : subject.kind === 'dispute') && (
                  <KeyValueList
                    items={[
                      {
                        label: t('refunds.drawer.ride'),
                        value: rideId !== undefined ? (
                          <Button variant="ghost" onClick={() => navigate('ride_detail', { id: rideId })}>
                            {detail?.subjectCard?.ride !== undefined ? t('refunds.drawer.ride.code', { code: detail.subjectCard.ride.code }) : t('refunds.drawer.ride.open')}
                          </Button>
                        ) : t('refunds.drawer.ride.none'),
                      },
                    ]}
                  />
                )}
                {tk !== undefined && (
                  <Stack gap={3}>
                    <SectionTitle>{t('refunds.drawer.people')}</SectionTitle>
                    {detail?.subjectCard === undefined || detail.subjectCard.parties.length === 0 ? <Notice tone="neutral">{t('refunds.drawer.people.none')}</Notice> : <SubjectCard card={detail.subjectCard} />}
                  </Stack>
                )}
              </Stack>
            </TabPanel>
            <TabPanel id="refund-drawer" tab="history" value={drawerTab}>
              <KeyValueList
                items={[
                  { label: t('refunds.drawer.history.requested'), value: <span>{actor(r.requestedBy, r.requestedByLabel)} · {when(r.requestedAt, r.marketId)}</span> },
                  r.decidedBy !== null
                    ? { label: t('refunds.drawer.history.decided', { status: v('refund.approval_status', r.status) }), value: <span>{actor(r.decidedBy, r.decidedByLabel)} · {when(r.decidedAt, r.marketId)}</span> }
                    : { label: t('refunds.drawer.status'), value: r.status === 'PENDING' ? t('refunds.drawer.history.waiting') : v('refund.approval_status', r.status) },
                  ...(r.decisionReason !== null ? [{ label: t('refunds.drawer.history.reason'), value: r.decisionReason }] : []),
                  { label: t('refunds.drawer.history.outcome'), value: t(`refunds.drawer.outcome.${outcome}`) },
                ]}
              />
            </TabPanel>
          </DrawerStack>
        </DrawerBody>
      </>
    );
  };

  return (
    <>
      <PageHeader title={t('refunds.title')} subtitle={t('refunds.subtitle')} />
      <Panel>
        <Stack gap={4}>
          <Text as="p" tone="secondary">{t('refunds.explain')}</Text>
          <FormRow columns="minmax(0, 320px)">
            <Field label={t('refunds.field.status')} htmlFor="refund-status">
              <Select id="refund-status" value={status} onChange={(e) => setStatus(e.target.value as RefundQueueStatus)}>
                {REFUND_QUEUE_STATUSES.map((s) => (
                  <option key={s} value={s}>{v('refund.approval_status', s)}</option>
                ))}
              </Select>
            </Field>
          </FormRow>
          {error !== undefined && <Notice tone="danger">{error}</Notice>}
        </Stack>
      </Panel>

      {totals.length > 0 && (
        <Grid min="sm" gap={4}>
          {totals.map((tot) => (
            <StatCard key={tot.currency} label={t('refunds.kpi.total', { currency: tot.currency })} value={money({ amountMinor: tot.totalMinor, currency: tot.currency })} hint={t('refunds.kpi.count', { count: String(tot.count) })} />
          ))}
        </Grid>
      )}

      {msg !== undefined && (
        <Notice tone={msg.tone}>{msg.text}</Notice>
      )}

      <Panel padded={false}>
        <PanelBody>
          <Field label={t('refunds.reason')} htmlFor="refund-reason" hint={t('refunds.reason.hint')}>
            <TextInput id="refund-reason" value={reason} maxLength={500} placeholder={t('ui.reason.placeholder')} onChange={(e) => setReason(e.target.value)} />
          </Field>
        </PanelBody>
<Divider />
        <Table
          columns={[
            {
              key: 'kind',
              header: t('refunds.col.kind'),
              render: (r: RefundApprovalView) => (
                <Stack gap={1} align="start">
                  <Text>{v('payments.refund_approval_kind', r.kind)}</Text>
                  <Badge tone={refundStatusTone(r.status)}>{v('refund.approval_status', r.status)}</Badge>
                </Stack>
              ),
            },
            {
              key: 'subject',
              header: t('refunds.col.subject'),
              render: (r: RefundApprovalView) => (
                <Button variant="ghost" onClick={() => openDrawer(r)} aria-label={t('refunds.subject.open', { subject: subjectLabel(r) })}>
                  <span className="tnum">{subjectLabel(r)}</span>
                </Button>
              ),
            },
            { key: 'amount', header: t('refunds.col.amount'), numeric: true, render: (r: RefundApprovalView) => <span className="tnum">{money(r.amount)}</span> },
            {
              key: 'threshold',
              header: t('refunds.col.threshold'),
              render: (r: RefundApprovalView) => (
                <Stack gap={1}>
                  <Text>{r.threshold !== null ? <span className="tnum">{money(r.threshold)}</span> : t('refunds.threshold.none')}</Text>
                  <Text role="caption" tone="tertiary">{routeReason(r.routeReason)}</Text>
                </Stack>
              ),
            },
            {
              key: 'requested',
              header: t('refunds.col.requestedBy'),
              render: (r: RefundApprovalView) => (
                <Stack gap={1}>
                  <Text>{actor(r.requestedBy, r.requestedByLabel)}</Text>
                  <Text role="caption" tone="tertiary" mono>{when(r.requestedAt, r.marketId)}</Text>
                </Stack>
              ),
            },
            {
              key: 'decided',
              header: t('refunds.col.decidedBy'),
              render: (r: RefundApprovalView) => {
                const who = actor(r.decidedBy, r.decidedByLabel);
                if (who === undefined) return <Text role="caption" tone="tertiary">{r.status === 'PENDING' ? t('refunds.actor.pending') : '—'}</Text>;
                return (
                  <Stack gap={1}>
                    <Text>{who}</Text>
                    <Text role="caption" tone="tertiary" mono>{when(r.decidedAt, r.marketId)}</Text>
                    <SelfApprovalNote record={r} />
                    {r.decisionReason !== null && <Text role="caption" tone="tertiary">{r.decisionReason}</Text>}
                  </Stack>
                );
              },
            },
            {
              key: 'act',
              header: '',
              render: (r: RefundApprovalView) => {
                if (r.status !== 'PENDING') return <span />;
                const block = refundApprovalBlock(r, operatorId, mayDecide, selfApproval);
                const own = block === 'OWN_REQUEST';
                return (
                  <Stack gap={2} align="end">
                    <Cluster gap={2} justify="end">
                      {block === undefined && (
                        <Button variant="secondary" disabled={busy || !hasReason} onClick={() => void decide(r, 'APPROVE')}>{t('refunds.approve')}</Button>
                      )}
                      {refundRejectable(r, operatorId, mayDecide) && (
                        <Button variant="danger" disabled={busy || !hasReason} onClick={() => void decide(r, 'REJECT')}>{own ? t('refunds.withdraw') : t('refunds.reject')}</Button>
                      )}
                    </Cluster>
                    {block !== undefined && block !== 'NOT_PENDING' && (
                      <NoteText compact>{v('refund.decision_block', block)}</NoteText>
                    )}
                  </Stack>
                );
              },
            },
          ]}
          rows={rows ?? []}
          rowKey={(r) => r.approvalId}
          loading={rows === undefined && error === undefined}
          loadingLabel={t('ui.loading')}
          error={error !== undefined ? t('ui.loadFailed') : undefined}
          onRetry={() => void load(status)}
          retryLabel={t('ui.retry')}
          empty={<EmptyState title={t('refunds.queue.empty')} />}
        />
      </Panel>

      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} labelledBy="refund-drawer-title">
        {drawerRow !== undefined && renderDrawer(drawerRow)}
      </Drawer>
    </>
  );
}
